import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useHistory, useLocation } from 'react-router-dom';

import Loading from '../components/Loading';
import API from '../repository';
import AuthClientUtils from '../utils/Auth/auth-client';

const AuthContext = createContext();

const AuthContextProvider = (props) => {
  const route = useHistory();
  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const callbackToken = query.get('token');
  const callbackLoading = query.get('loading');

  const [user, setUser] = useState(null);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isLoadingAnimation, setIsLoadingAnimation] = useState(true);
  const [isLoadingAnimationDisplay, setIsLoadingAnimationDisplay] =
    useState(true);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [isUpdatingActive, setisUpdatingActive] = useState({
    localRoulette: false,
    internationalRoulette: false,
  });
  const [authError, setAuthError] = useState(null);

  const onToggleChange = useCallback(
    async ({ localRoulette, internationalRoulette }) => {
      const postToggleState = {
        localRoulette: async () => {
          setisUpdatingActive({ localRoulette: true });
          await API().patch({ path: 'user/active', body: { localRoulette } });
          const data = await API().get({ path: 'me' });
          setUser(data);
          setisUpdatingActive({ localRoulette: false });
        },
        internationalRoulette: async () => {
          setisUpdatingActive({ internationalRoulette: true });
          await API().patch({
            path: 'user/active',
            body: { internationalRoulette },
          });
          const data = await API().get({ path: 'me' });
          setUser(data);
          setisUpdatingActive({ internationalRoulette: false });
        },
      }[
        (localRoulette && 'localRoulette') ||
          (internationalRoulette && 'internationalRoulette')
      ];

      await postToggleState();
    },
    [setisUpdatingActive, setUser],
  );

  const onConfigureTeam = useCallback(
    async (team) => {
      const postConfigureTeam = async (team) => {
        setIsLoadingResponse(true);
        await API().patch({ path: 'user/team', body: { team } });
        const data = await API().get({ path: 'me' });
        setUser(data);
      };
      await postConfigureTeam(team);
      setIsLoadingResponse(false);
    },
    [setIsLoadingResponse, setUser],
  );

  const loginHandler = useCallback(
    async (callbackToken, callbackLoading) => {
      try {
        setIsLoadingAnimation(true);
        setIsLoadingPage(true);
        const url = `me${callbackLoading === 'true' ? '/requestPicture' : ''}`;
        if (callbackToken) AuthClientUtils.setToken(callbackToken);
        const data = await API().get({ path: url });

        setUser(data);
        setAuthError(null);
        route.push('/');
        setIsLoadingPage(false);
        setTimeout(() => {
          setIsLoadingAnimation(false);
          setTimeout(() => {
            setIsLoadingAnimationDisplay(false);
          }, 1000);
        }, 2000);
      } catch (error) {
        setUser(null);
        setAuthError(error);
        route.push('/error');
        setIsLoadingPage(false);
        AuthClientUtils.clearStorage();
      }
    },
    [route],
  );

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  useEffect(() => {
    if (callbackToken && callbackLoading)
      loginHandler(callbackToken, callbackLoading);
  }, [loginHandler, callbackToken, callbackLoading]);

  useEffect(() => {
    if (AuthClientUtils.isLoggedIn()) loginHandler();
  }, [loginHandler]);

  const logOut = useCallback(
    (response) => {
      AuthClientUtils.clearStorage();
      setUser(null);
      route.push('/login');
    },
    [route],
  );

  const value = useMemo(
    () => ({
      user,
      logOut,
      authError,
      clearAuthError,
      onToggleChange,
      onConfigureTeam,
      isLoadingPage,
      isLoadingAnimation,
      isLoadingResponse,
      isUpdatingActive,
      isLoadingAnimationDisplay,
    }),
    [
      user,
      logOut,
      authError,
      clearAuthError,
      onToggleChange,
      onConfigureTeam,
      isLoadingPage,
      isLoadingAnimation,
      isLoadingResponse,
      isUpdatingActive,
      isLoadingAnimationDisplay,
    ],
  );

  if (isLoadingPage) {
    return <Loading />;
  }

  return <AuthContext.Provider {...props} value={value} />;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthContextProvider`);
  }
  return context;
};

export { useAuth, AuthContextProvider };
